import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Wallet, Package, TrendingUp, CalendarDays, Clock, HandCoins } from "lucide-react";
import { rpc } from "@/lib/supabase";
import { PageHeader, Stat, EmptyState, Loader, Card } from "@/components/kit";
import { formatCOP, formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const REQ_BADGE = {
  PENDIENTE: "text-amber-400 border-amber-500/40 bg-amber-500/10",
  EN_PROCESO: "text-blue-400 border-blue-500/40 bg-blue-500/10",
  PAGADA: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
  RECHAZADA: "text-red-400 border-red-500/40 bg-red-500/10",
};

export default function DriverEarnings() {
  const [s, setS] = useState(null);
  const [reqs, setReqs] = useState([]);
  const [detail, setDetail] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    Promise.all([rpc("get_my_earnings_summary"), rpc("get_driver_earnings"), rpc("get_my_payout_requests")])
      .then(([sum, earn, r]) => { setS(sum); setDetail(earn); setReqs(r || []); })
      .catch(() => setS({}));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!s) return <Loader />;

  return (
    <div>
      <PageHeader title="Ganancias" subtitle="Tu 75% por cada entrega completada." testId="driver-earnings" />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Hoy" value={formatCOP(s.hoy)} icon={TrendingUp} testId="earn-hoy" />
        <Stat label="Últimos 7 días" value={formatCOP(s.ultimos_7)} icon={CalendarDays} testId="earn-7" />
        <Stat label="Mes actual" value={formatCOP(s.mes_actual)} icon={CalendarDays} testId="earn-mes" />
        <Stat label="Total histórico" value={formatCOP(s.total)} icon={Wallet} testId="earn-total" />
      </div>

      <Card className="mt-6 p-5" data-testid="balance-card">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-gray-500">Saldo disponible para cobrar</div>
            <div className="mt-1 font-heading text-4xl font-extrabold text-[#FF6A00]" data-testid="balance-available">{formatCOP(s.disponible)}</div>
            <div className="mt-1 text-xs text-gray-400">
              Reservado en solicitudes: <span data-testid="balance-reserved">{formatCOP(s.reservado)}</span> · Pagado total: {formatCOP(s.pagado_total)}
            </div>
          </div>
          <Button
            className="bg-[#FF6A00] font-extrabold text-black hover:bg-[#ff7d1a] disabled:opacity-40"
            disabled={Number(s.disponible) <= 0}
            onClick={() => setOpen(true)}
            data-testid="request-payout-btn"
          >
            <HandCoins size={16} className="mr-2" /> Solicitar cobro
          </Button>
        </div>
      </Card>

      <h3 className="mb-3 mt-8 font-heading text-lg font-bold text-white">Historial de cobros</h3>
      {reqs.length === 0 ? (
        <EmptyState icon={HandCoins} title="Sin solicitudes de cobro" subtitle="Tu saldo disponible aparecerá aquí cuando solicites un cobro." testId="empty-requests" />
      ) : (
        <Card className="p-0" data-testid="requests-list">
          {reqs.map((r, i) => (
            <div key={r.id} className={`flex items-center justify-between px-5 py-4 ${i > 0 ? "border-t border-[#1e1e21]" : ""}`} data-testid={`request-row-${r.id}`}>
              <div>
                <div className="font-heading font-bold text-white">{formatCOP(r.amount)} <span className="ml-2 text-xs font-normal text-gray-400">{r.method}</span></div>
                <div className="text-xs text-gray-500">{formatDateTime(r.requested_at)}{r.reject_reason && ` · ${r.reject_reason}`}</div>
              </div>
              <span className={`rounded-full border px-2.5 py-1 text-xs font-bold ${REQ_BADGE[r.status]}`}>{r.status}</span>
            </div>
          ))}
        </Card>
      )}

      <h3 className="mb-3 mt-8 font-heading text-lg font-bold text-white">Detalle por entrega</h3>
      {(!detail?.services || detail.services.length === 0) ? (
        <EmptyState icon={Wallet} title="Sin ganancias todavía" subtitle="Completa entregas para acumular ingresos." testId="empty-earnings" />
      ) : (
        <Card className="p-0">
          {detail.services.map((sv, i) => (
            <div key={sv.id} className={`flex items-center justify-between px-5 py-4 ${i > 0 ? "border-t border-[#1e1e21]" : ""}`} data-testid={`earning-row-${sv.id}`}>
              <div className="flex items-center gap-3">
                <div className="grid h-9 w-9 place-items-center rounded-lg bg-[#18181B] text-[#FF6A00]"><Package size={16} /></div>
                <div>
                  <div className="text-sm font-medium text-white">{sv.dropoff_address}</div>
                  <div className="text-xs text-gray-500">{formatDateTime(sv.delivered_at)}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-heading font-bold text-emerald-400">{formatCOP(sv.driver_earning)}</div>
                <div className="text-xs text-gray-500">de {formatCOP(sv.quoted_price)}</div>
              </div>
            </div>
          ))}
        </Card>
      )}

      <RequestDialog open={open} onClose={() => setOpen(false)} available={Number(s.disponible)} onDone={load} />
    </div>
  );
}

function RequestDialog({ open, onClose, available, onDone }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("NEQUI");
  const [holder, setHolder] = useState("");
  const [idNum, setIdNum] = useState("");
  const [account, setAccount] = useState("");
  const [phone, setPhone] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setAmount(""); setMethod("NEQUI"); setHolder(""); setIdNum(""); setAccount(""); setPhone(""); setConfirmed(false);
      rpc("get_my_payout_account").then((a) => {
        if (a) { setMethod(a.method); setHolder(a.holder_full_name || ""); }
      }).catch(() => {});
    }
  }, [open]);

  const submit = async () => {
    setBusy(true);
    try {
      const res = await rpc("create_payout_request", {
        p_amount: Number(amount), p_method: method,
        p_holder_name: holder || null, p_id_number: idNum || null,
        p_account_number: method === "BANCOLOMBIA" ? account : null,
        p_phone_number: method === "NEQUI" ? phone : null,
      });
      toast.success(`Solicitud creada. Saldo restante: ${formatCOP(res.disponible_restante)}`);
      onDone(); onClose();
    } catch (e) { toast.error(e?.message || "Error"); } finally { setBusy(false); }
  };

  const invalid = !amount || Number(amount) <= 0 || Number(amount) > available || !confirmed ||
    (method === "NEQUI" && !phone.trim()) || (method === "BANCOLOMBIA" && !account.trim());

  const field = "border-[#27272A] bg-[#0d0d0f]";
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-[#27272A] bg-[#141416] text-white" data-testid="request-dialog">
        <DialogHeader>
          <DialogTitle>Solicitar cobro</DialogTitle>
          <DialogDescription>Disponible: <b className="text-[#FF6A00]">{formatCOP(available)}</b>. El pago lo realiza RAPIFAST manualmente.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input type="number" placeholder="Monto a solicitar" value={amount} onChange={(e) => setAmount(e.target.value)} className={field} data-testid="request-amount" />
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger className={field} data-testid="request-method"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="NEQUI">Nequi</SelectItem>
              <SelectItem value="BANCOLOMBIA">Bancolombia</SelectItem>
              <SelectItem value="CASH">Efectivo</SelectItem>
            </SelectContent>
          </Select>
          <Input placeholder="Nombre del titular" value={holder} onChange={(e) => setHolder(e.target.value)} className={field} data-testid="request-holder" />
          <Input placeholder="Cédula" value={idNum} onChange={(e) => setIdNum(e.target.value)} className={field} data-testid="request-id" />
          {method === "BANCOLOMBIA" && <Input placeholder="Número de cuenta" value={account} onChange={(e) => setAccount(e.target.value)} className={field} data-testid="request-account" />}
          {method === "NEQUI" && <Input placeholder="Celular Nequi" value={phone} onChange={(e) => setPhone(e.target.value)} className={field} data-testid="request-phone" />}
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} data-testid="request-confirm" />
            Confirmo que quiero solicitar este cobro
          </label>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy || invalid} className="bg-[#FF6A00] font-extrabold text-black hover:bg-[#ff7d1a] disabled:opacity-40" data-testid="request-submit">Enviar solicitud</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
